import * as authAPI from "@/API/auth.js";

const getDefaultState = () => {
  return {
    status: "",
    auth_token: "",
    referrerToken: "",
    stay_logged_in: true,
    acpMode: false,
    lpBonusName: null,
    otpEmail: "",
    otpMode: null, // "register" | "login"
    otpResendAvailableAt: null, // ISO string, survives vuex-persist reload
  };
};

const OTP_DEFAULT_COOLDOWN_SECS = 60;

// initial state
const state = getDefaultState();

const mutations = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  auth_request(state) {
    state.status = "loading";
  },
  auth_success(state) {
    state.status = "success";
  },
  auth_pending(state, token) {
    state.status = "pending";
    state.auth_token = "Bearer " + token;
  },
  auth_error(state) {
    state.status = "error";
    state.auth_token = "";
  },

  setReferrerToken(state, string) {
    state.referrerToken = string;
  },
  setStayLoggedIn(state, boolean) {
    state.stay_logged_in = boolean;
  },
  setAcpMode: (state, boolean) => (state.acpMode = boolean),
  setLpBonusName: (state, payload) => (state.lpBonusName = payload),

  setOtpEmail: (state, string) => (state.otpEmail = string),
  setOtpMode: (state, string) => (state.otpMode = string),
  setOtpResendAvailableAt: (state, iso) => (state.otpResendAvailableAt = iso),
  clearOtpFlow(state) {
    state.otpEmail = "";
    state.otpMode = null;
    state.otpResendAvailableAt = null;
  },
};

const getters = {
  isLoggedIn: (state) => !!state.auth_token && state.status === "success",
  authStatus: (state) => state.status,
  userToken: (state) => state.auth_token,
};

const actions = {
  resetAuthState({ commit }) {
    commit("resetState");
  },
  logUserIn({ commit, dispatch, rootGetters }, payload) {
    return new Promise((resolve, reject) => {
      commit("auth_request");
      const localeQuery =
        rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();
      authAPI
        .fetchLoggedInUser(payload, localeQuery)
        .then((res) => {
          dispatch("setUserData", res);
          dispatch("setUserTimers", res);

          resolve(res);
        })
        .catch((err) => {
          console.log(err);
          commit("auth_error");
          commit("user/updateUserObject", null, { root: true });

          reject(err);
        });
    });
  },
  acpLogin({ commit, dispatch }, payload) {
    dispatch("setUserTimers", payload);
    const user = payload.data.data.user;
    commit("auth_success");
    commit("setAcpMode", true);
    commit("user/updateUserObject", user, { root: true });
  },

  setUserData({ commit }, payload) {
    const token = payload.data.data.personal_access_token;
    // as of now, the entire user object is sent to the store
    // so it can be accessed anywhere within the app after successful login
    const user = payload.data.data.user;
    commit("auth_pending", token);
    commit("auth_success");
    commit("user/updateUserObject", user, { root: true });
  },

  setUserTimers({ commit }, payload) {
    commit(
      "bonusslot/setBonusSlotEndDate",
      payload.data?.data?.bonus_slot_remaining_secs || 0,
      { root: true }
    );

    commit("user/setExpboostEndDate", new Date().toISOString(), {
      root: true,
    });

    const timers = payload.data?.data?.user?.usertimers || [];
    for (let i = 0; i < timers.length; i++) {
      if (timers[i].type === "EXPBOOSTER") {
        let expTimerEnd = timers[i].end_at;
        if (expTimerEnd) {
          commit("user/setExpboostEndDate", expTimerEnd, {
            root: true,
          });
        }
      }
    }
  },
  // the v2 docs describe a `payload` envelope but the live dev API answers with
  // v1's `data`; accept either rather than storing an undefined token
  setUserDataV2({ commit }, res) {
    const body = res.data?.payload ?? res.data?.data ?? {};
    const token = body.personal_access_token;
    const user = body.user;

    // never commit auth_success without a token: that leaves the app
    // "logged in" with `Bearer undefined` and no user object
    if (!token) {
      throw new Error("otp/verify returned no personal_access_token");
    }

    commit("auth_pending", token);
    commit("auth_success");
    commit("user/updateUserObject", user, { root: true });
  },

  setUserTimersV2({ commit }, res) {
    const body = res.data?.payload ?? res.data?.data ?? {};

    commit(
      "bonusslot/setBonusSlotEndDate",
      body.bonus_slot_remaining_secs || 0,
      {
        root: true,
      }
    );

    commit("user/setExpboostEndDate", new Date().toISOString(), {
      root: true,
    });

    const timers = body.user?.usertimers || [];
    for (let i = 0; i < timers.length; i++) {
      if (timers[i].type === "EXPBOOSTER" && timers[i].end_at) {
        commit("user/setExpboostEndDate", timers[i].end_at, { root: true });
      }
    }
  },

  otpRequestRegister({ state, commit, dispatch, rootGetters }, payload) {
    const localeQuery =
      rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();

    const body = {
      email: payload.email,
      tac: payload.tac,
      newsletter: payload.newsletter,
    };

    if (state.referrerToken) {
      body.startapp = state.referrerToken;
    }
    if (state.lpBonusName) {
      body.source = state.lpBonusName;
      body.medium = "exit_intend";
    }

    return authAPI
      .otpRegister(body, localeQuery)
      .then((res) => {
        commit("setOtpEmail", payload.email);
        commit("setOtpMode", "register");
        dispatch("otpStartCooldown", null);
        return res;
      })
      .catch((err) => {
        if (err?.status === 429) {
          const alreadySent = state.otpEmail === payload.email;
          if (alreadySent) {
            dispatch("otpStartCooldown", err?.headers?.["retry-after"]);
          }
        }
        return Promise.reject(err);
      });
  },

  otpRequestLogin({ state, commit, dispatch, rootGetters }, email) {
    const localeQuery =
      rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();

    return authAPI
      .otpLogin(email, localeQuery)
      .then((res) => {
        commit("setOtpEmail", email);
        commit("setOtpMode", "login");
        dispatch("otpStartCooldown", null);
        return res;
      })
      .catch((err) => {
        if (err?.status === 429) {
          const alreadySent = state.otpEmail === email;
          if (alreadySent) {
            dispatch("otpStartCooldown", err?.headers?.["retry-after"]);
          }
        }
        return Promise.reject(err);
      });
  },

  otpVerifyCode({ state, commit, dispatch, rootGetters }, code) {
    const localeQuery =
      rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();
    commit("auth_request");

    return authAPI
      .otpVerify(state.otpEmail, code, localeQuery)
      .then((res) => {
        // setUserDataV2 throws on a tokenless body, so a malformed 200 must
        // reject here instead of reporting a successful login
        return dispatch("setUserDataV2", res).then(() => {
          dispatch("setUserTimersV2", res);
          commit("clearOtpFlow");
          return res;
        });
      })
      .catch((err) => {
        // 422 otp.invalid is a retry, not a dead session:
        // keep otpEmail so the user stays on the code step
        commit("auth_error");
        return Promise.reject(err);
      });
  },

  // Retry-After is seconds; fall back to the documented 60s cooldown.
  // Without a real Retry-After a guessed target must never push an existing
  // deadline further out, or every blocked attempt extends the user's wait.
  otpStartCooldown({ state, commit }, retryAfter) {
    const parsed = parseInt(retryAfter, 10);
    const secs = isNaN(parsed) ? OTP_DEFAULT_COOLDOWN_SECS : parsed;
    const until = new Date(Date.now() + secs * 1000).toISOString();
    // a still-pending target came from the request that actually sent the
    // mail, so it is closer to the truth than another now+60 guess
    const current = state.otpResendAvailableAt;
    const keepCurrent =
      isNaN(parsed) && !!current && new Date(current) > new Date();

    if (!keepCurrent) commit("setOtpResendAvailableAt", until);
  },

  logUserInFromLandingPage({ commit, dispatch }, payload) {
    commit("auth_request");
    return new Promise((resolve) => {
      dispatch("setUserData", payload);
      dispatch("setUserTimers", payload);
      resolve();
    });
  },
  registerUser({ commit, dispatch, rootGetters }, payload) {
    return new Promise((resolve, reject) => {
      commit("auth_request");
      const localeQuery =
        rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();
      authAPI
        .registerNewUser(payload, localeQuery)
        .then((res) => {
          dispatch("setUserData", res);
          resolve(res);
        })
        .catch((err) => {
          commit("auth_error");
          commit("user/updateUserObject", null, { root: true });
          reject(err);
        });
    });
  },
  facebookLogin({ commit, dispatch, rootGetters }, oAuthToken) {
    return new Promise((resolve, reject) => {
      commit("auth_request");
      const localeQuery =
        rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();
      authAPI
        .loginWithFacebook(oAuthToken, localeQuery)
        .then((res) => {
          dispatch("setUserData", res);
          resolve(res);
        })
        .catch((err) => {
          console.log(err);
          commit("auth_error");
          commit("user/updateUserObject", null, { root: true });
          reject(err);
        });
    });
  },
  googleLogin({ commit, dispatch, rootGetters }, accessToken) {
    return new Promise((resolve, reject) => {
      commit("auth_request");
      const localeQuery =
        rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();

      authAPI
        .loginWithGoogle(accessToken, localeQuery)
        .then((res) => {
          dispatch("setUserData", res);
          dispatch("setUserTimers", res);
          resolve(res);
        })
        .catch((err) => {
          console.log(err);
          commit("auth_error");
          commit("user/updateUserObject", null, { root: true });
          reject(err);
        });
    });
  },
  paypalLogin({ commit, dispatch, rootGetters }, code) {
    return new Promise((resolve, reject) => {
      commit("auth_request");
      const localeQuery =
        rootGetters.getLocale + "_" + rootGetters.getLocale.toUpperCase();

      authAPI
        .loginWithPaypal(code, localeQuery)
        .then((res) => {
          dispatch("setUserData", res);
          dispatch("setUserTimers", res);
          resolve(res);
        })
        .catch((err) => {
          console.log(err);
          commit("auth_error");
          commit("user/updateUserObject", null, { root: true });
          reject(err);
        });
    });
  },
};
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
